import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  Suspense,
  lazy,
} from 'react';

const InspirationPagesDialog = lazy(() =>
  import('@/components/dialogs/InspirationPages').then((module) => ({
    default: module.InspirationPagesDialog,
  })),
);

export type EditorDeserializeFn = (json: string) => void;

interface InspirationPagesContextValue {
  isOpen: boolean;
  openInspirationPages: () => void;
  closeInspirationPages: () => void;
  registerEditorDeserializer: (fn: EditorDeserializeFn) => void;
  deserializeEditor: EditorDeserializeFn;
}

export const InspirationPagesContext = createContext<InspirationPagesContextValue | null>(null);

interface InspirationPagesProviderProps {
  children: React.ReactNode;
}

export const InspirationPagesProvider: React.FC<InspirationPagesProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const editorDeserializeRef = useRef<EditorDeserializeFn | null>(null);

  const openInspirationPages = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeInspirationPages = useCallback(() => {
    setIsOpen(false);
  }, []);

  const registerEditorDeserializer = useCallback((fn: EditorDeserializeFn) => {
    editorDeserializeRef.current = fn;
  }, []);

  const deserializeEditor = useCallback((json: string) => {
    editorDeserializeRef.current?.(json);
  }, []);

  const value = useMemo<InspirationPagesContextValue>(
    () => ({
      isOpen,
      openInspirationPages,
      closeInspirationPages,
      registerEditorDeserializer,
      deserializeEditor,
    }),
    [
      isOpen,
      openInspirationPages,
      closeInspirationPages,
      registerEditorDeserializer,
      deserializeEditor,
    ],
  );

  return (
    <InspirationPagesContext.Provider value={value}>
      {children}
      {isOpen && (
        <Suspense fallback={null}>
          <InspirationPagesDialog isOpen={isOpen} onClose={closeInspirationPages} />
        </Suspense>
      )}
    </InspirationPagesContext.Provider>
  );
};
